<!--@format-->
<template>
    <div class="add-task-widget">
        <div class="widget-header">
            <h3>{{ steps[step].title || 'Tasks' }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="step === 'addTask' || step === 'editTask'" class="add-task-step">
                <div class="task-form-row">
                    <div class="input-container">
                        <app-input
                            v-model="task.title"
                            label="Title"
                            placeholder="Type a name..."
                        />
                    </div>
                    <div class="input-container">
                        <label>Task Type</label>
                        <task-type-search v-model="task.type" />
                    </div>
                    <div class="input-container">
                        <label>Choose a contact</label>
                        <contact-search
                            id="contact-search"
                            v-model="task.contact"
                            :clientId="clientId"
                            :initialId="task.contactId"
                        />
                    </div>
                    <div
                        class="input-container"
                        :class="{ 'form-group--error': !$v.task.user.required }"
                    >
                        <label>Who is responsible?</label>
                        <user-search
                            id="user-search"
                            v-model="task.user"
                            :clientId="clientId"
                            :initialId="task.userId"
                        />
                        <div v-if="!$v.task.user.required" class="error">
                            A user must be selected
                        </div>
                    </div>
                    <div class="input-container">
                        <label>When is it due?</label>
                        <date-time-selector
                            :value="task.fullDueDate"
                            @dateSelected="setDueDate"
                            @timeSelected="setDueDateTime"
                            @dateRemoved="removeDueDate"
                            @timeRemoved="removeDueDateTime"
                        />
                    </div>
                    <div class="input-container">
                        <label>Description</label>
                        <froala v-model="task.description" :config="config" />
                    </div>
                </div>
            </div>
            <div
                v-else-if="step === 'saving' || step === 'loading'"
                class="add-task-step saving-step"
            >
                <quick-add-saving-svg />
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :onClick="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button :onClick="steps[step].action" :loading="steps[step].loading">
                    {{ steps[step].buttonText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input');
const QuickAddSavingSvg = () => import('@/components/svg/quick-add/quick-add-saving-svg.vue');
const ContactSearch = () => import('@/components/contact/contact-search.vue');
const UserSearch = () => import('@/components/user/user-search.vue');
const DateTimeSelector = () => import('@/components/common/date-time-selector.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const TaskTypeSearch = () => import('@/components/tasks/task-type-search');

import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import { LogLevel } from "@/scripts/logger";

export default {
    name: 'quick-add-task-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'quick-add-saving-svg': QuickAddSavingSvg,
        'contact-search': ContactSearch,
        'user-search': UserSearch,
        'date-time-selector': DateTimeSelector,
        'widget-close-svg': WidgetCloseSvg,
        'task-type-search': TaskTypeSearch,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            isInitializing: true,

            task: {
                id: null,
                title: null,
                type: null,
                contact: null,
                user: null,
                dueDate: null,
                dueTime: null,
                fullDueDate: null,
                description: '',
                contactId: this.options.contactId,
                userId: this.options.userId,
            },

            contacts: [],
            contact: null,
            searching: false,

            users: null,

            contactId: null,

            step: null,

            steps: {
                loading: {
                    title: 'Loading',
                    buttonText: 'Loading',
                    action: null,
                    loading: true,
                },

                addTask: {
                    title: 'Add Task',
                    buttonText: 'Add Task',
                    action: this.saveTask,
                    loading: false,
                },

                editTask: {
                    title: 'Edit Task',
                    buttonText: 'Save Changes',
                    action: this.saveChanges,
                    loading: false,
                },

                saving: {
                    title: 'Saving Task',
                    buttonText: 'Saving',
                    action: null,
                    loading: true,
                },
            },
        };
    },

    validations: {
        task: {
            user: {
                required,
            },
        },
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },

        dueDate() {
            if (this.task.dueDate && this.task.dueTime) {
                return `${this.task.dueDate}T${this.task.dueTime}`;
            } else if (this.task.dueDate && !this.task.dueTime) {
                return this.task.dueDate + 'T08:00:00';
            } else if (!this.task.dueDate && this.task.dueTime) {
                return `${this.$moment().add(1, 'days').format('YYYY-MM-DD')}T${this.task.dueTime}`;
            } else {
                return `${this.$moment().add(1, 'days').startOf('day').add(8, 'hours').format()}`;
            }
        },

        config() {
            return {
                key: this.$helpers.getFroalaKey(),
                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],
                requestHeaders: {
                    Authorization: 'Bearer ' + localStorage.jwt,
                },
                placeholderText: 'Type Away...',
                toolbarButtons: ['bold', 'italic', 'insertLink', 'align'],
            };
        },

        isClientUser() {
            return this.$store.getters.isClientUser;
        },

        userId() {
            return this.$store.state.user.id;
        },

        isAdmin() {
            return this.$store.getters.isAdmin;
        },
    },

    created() {
        this.step = 'addTask';

        if (this.options && this.options.contact) {
            this.task.contact = this.options.contact;
        }

        if (this.isClientUser) {
            this.task.user = this.options.user || null;
        }

        this.initializeEditMode();
    },

    methods: {
        initializeEditMode() {
            if (this.options && this.options.taskId) {
                if (this.options.contactId) {
                    this.contactId = this.options.contactId;
                }

                this.step = 'editTask';
                this.getData();
            }
        },

        getData() {
            this.getTask();
        },

        getTask() {
            this.$api
                .get(
                    `/service/UserTasks?$filter=Id eq ${this.options.taskId}&$expand=Contact($select=Id,FirstName,LastName,ProfileImageUrl;$expand=Company($select=Id,Name)),AssignedUser($select=Id,FirstName,LastName,ProfileImageUrl),CreatedByUser($select=Id,FirstName,LastName,ProfileImageUrl)`
                )
                .then((response) => {
                    this.createTask(response.data.value[0]);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'There was an error getting your task information.',
                        { error },
                    );
                });
        },

        createTask(responseTask) {
            this.task.id = responseTask.Id;
            this.task.title = responseTask.Title;
            this.task.type = responseTask.TaskType;
            this.task.contact = this.createContact(responseTask.Contact);
            this.task.user = this.createUser(responseTask.AssignedUser);
            this.task.fullDueDate = responseTask.DueDate;
            this.task.dueDate = null;
            this.task.dueTime = null;
            this.task.description = responseTask.Description;
        },

        createContact(rawContact) {
            if (rawContact) {
                return {
                    id: rawContact.Id,
                    email: rawContact.Email,
                    name: this.$helpers.getName(rawContact.FirstName, rawContact.LastName),
                    imageUrl: rawContact.ImageUrl,
                };
            } else {
                return null;
            }
        },

        createUser(rawUser) {
            if (rawUser) {
                return {
                    id: rawUser.Id,
                    email: rawUser.Email,
                    name: this.$helpers.getName(rawUser.FirstName, rawUser.LastName),
                    imageUrl: rawUser.ProfileImageUrl || require('@/assets/images/avatar.png'),
                };
            } else {
                return null;
            }
        },

        goToAddTaskStep() {
            this.step = 'addTask';
        },

        clearTask() {
            this.task = {};
        },

        saveTask() {
            if (this.task.user) {
                this.step = 'saving';

                var task = {
                    Title: this.task.title,
                    DueDate: this.$moment(this.dueDate).format(),
                    Description: this.task.description,
                    TaskType: this.task.type || 'General',
                    IsClosed: false,
                    CreatedByUserId: this.$store.state.user.id,
                    AssignedUserId: this.task.user.id,
                    ContactId: this.task.contact ? `${this.task.contact.id}` : null,
                };

                this.$api
                    .post('/service/UserTasks', task)
                    .then(() => {
                        var iconUrl = this.getIconUrl();

                        var notification = {
                            iconUrl: iconUrl,
                            isIconSvg: true,
                            subIcon: 'success',
                            message: `A ${this.task.type ? this.task.type : 'Follow up'}${
                                this.task.contact ? ' with ' + this.task.contact.name : ''
                            } was just created.`,
                            actionLabel: 'View',
                            action: () => {
                                this.$router.push(`${this.routeBaseUrl}/Tasks`);
                                EventBus.$emit('close-notification', this.notification);
                            },
                        };

                        EventBus.$emit('task:update');
                        EventBus.$emit('close-widget', this.widget);
                        EventBus.$emit('show-notification', notification);
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while adding your task.',
                            { error },
                        );
                    });
            } else {
                this.$helpers.showFailNotification(
                    'Please select a user responsible for this task.',
                    { level: LogLevel.INFO },
                );
            }
        },

        saveChanges() {
            this.step = 'saving';

            if (this.task.user) {
                var request = {
                    Title: this.task.title,
                    DueDate: this.$moment(this.dueDate).isValid()
                        ? this.$moment(this.dueDate).format()
                        : null,
                    Description: this.task.description,
                    TaskType: this.task.type,
                    AssignedUserId: this.task.user.id,
                    CreatedByUserId: this.userId,
                    ContactId: this.task.contact ? this.task.contact.id : null,
                };

                this.$api
                    .odataPatch('/service/UserTasks(' + this.task.id + ')', request)
                    .then(() => {
                        var iconUrl = this.getIconUrl();
                        var message = `Task${
                            this.task.contact ? ' with ' + this.task.contact.name : ''
                        } has been updated.`;

                        var notification = {
                            iconUrl: iconUrl,
                            isIconSvg: true,
                            subIcon: 'success',
                            message: message,
                            actionLabel: 'View',
                            action: () => {
                                this.$router.push(`${this.routeBaseUrl}/Tasks`);
                                EventBus.$emit('close-notification', this.notification);
                            },
                        };

                        EventBus.$emit('task:update');
                        EventBus.$emit('close-widget', this.widget);
                        EventBus.$emit('show-notification', notification);
                    })
                    .catch((error) => {
                        this.step = 'editTask';
                        this.$helpers.showFailNotification(
                            'Failed to save your task changes. Please try again.',
                            { error },
                        );
                    });
            } else {
                this.step = 'editTask';
                this.$helpers.showFailNotification('Please select a user.', { level: LogLevel.INFO });
            }
        },

        getIconUrl() {
            switch (this.task.type) {
                case 'Call':
                    return require('@/assets/images/phone-stroke-icon.png');
                case 'Email':
                    return require('@/assets/images/email-stroke-icon.png');
                case 'Note':
                    return require('@/assets/images/note-stroke-icon.png');
                case 'Meeting':
                    return require('@/assets/images/meeting-stroke-icon.png');
                default:
                    return require('@/assets/images/SVG/notification-clip-board.svg');
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        setDueDate(date) {
            this.task.dueDate = date;
        },

        removeDueDate() {
            this.task.dueDate = null;
        },

        setDueDateTime(time) {
            this.task.dueTime = time;
        },

        removeDueDateTime() {
            this.task.dueTime = null;
        },
    },
};
</script>

<style>
.add-task-widget .form-group--error .error {
    font-size: 12px;
    color: #c00;
    margin-top: 2px;
}
</style>

<style scoped>
.add-task-widget .add-task-step {
    width: 100%;
}

.add-task-widget .add-task-step .task-form-row {
    display: flex;
    flex-direction: column;
}

.add-task-widget .add-task-step .task-form-row.task-profile-image .profile-image {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    margin-right: 35px;
}

.add-task-widget .add-task-step .task-form-row .input-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 24px;
}

.add-task-widget .add-task-step .task-form-row label {
    line-height: 19px;
    margin-bottom: 13px;
    font-weight: 500;
}

.add-task-widget .add-task-step .task-form-row input[type='text'],
.add-task-widget .add-task-step .task-form-row input[type='email'],
.add-task-widget .add-task-step .task-form-row input[type='tel'] {
    height: 25px;
    padding: 0px 0px 5px 9px;
    color: #382153;
    border: none;
    border-bottom: 2px solid #e9e9ef;
    outline: none;
}

.add-task-widget .add-task-step .task-form-row input[type='text']::placeholder,
.add-task-widget .add-task-step .task-form-row input[type='email']::placeholder,
.add-task-widget .add-task-step .task-form-row input[type='tel']::placeholder {
    font-size: 16px;
    line-height: 19px;
    color: #cac6cb;
}

.add-task-widget .add-task-step .task-form-row input[type='text']:focus,
.add-task-widget .add-task-step .task-form-row input[type='email']:focus,
.add-task-widget .add-task-step .task-form-row input[type='tel']:focus {
    border-bottom: 3px solid #18aeff;
}

.add-task-widget .add-task-step.scrub-step .task-form-row {
    flex-direction: column;
}

.add-task-widget .add-task-step.saving-step svg {
    width: 100%;
}

.add-task-widget .action-container {
    display: flex;
}

.add-task-widget .action-container .btn {
    margin-left: 15px;
}

.add-task-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.add-task-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.add-task-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}
</style>
